import PropTypes from 'prop-types';
import React, { PureComponent } from 'react';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import S3Upload from 'react-s3-uploader/s3upload';
import Dropzone from 'react-dropzone';
import CircularProgress from '@material-ui/core/CircularProgress';
import Button from '@material-ui/core/Button';
import Cropper from 'react-cropper';
import pica from 'pica/dist/pica';
import 'cropperjs/dist/cropper.css';
import Dialog from '@material-ui/core/Dialog';
import DialogTitle from '@material-ui/core/DialogTitle';
import DialogContent from '@material-ui/core/DialogContent';
import DialogActions from '@material-ui/core/DialogActions';
import IconButton from '@material-ui/core/IconButton';
import { AddBox, IndeterminateCheckBox } from '@mui/icons-material';

import * as Actions from 'containers/Notification/actions';
import { gray, orange, red } from 'utils/color-styles';
import ImageIcon from 'assets/icons/image.svg';
import Img from 'retired/Img';
import { getGetSignedUrl } from 'containers/FilesUpload/getGetSignedUrl';

class FileUpload extends PureComponent {
  state = {
    progress: null,
    error: null,
    uploadedFiles: [],
    selectedFiles: [],
  };

  cropper = null;

  handleProgress = (progress) => {
    this.setState({ progress });
  };

  handleError = (err) => {
    this.setState({ error: err, progress: null });
    this.props.actions.showErrorAction('An error occurred with the upload.');
  };

  handleFinish = (info, file) => {
    const uploadedFile = {
      file,
      ...info,
    };

    const { uploadedFiles } = this.state;
    uploadedFiles.push(uploadedFile);
    this.setState({ uploadedFiles, selectedFiles: [], error: null, progress: null }, () => {
      this.props.onFinish(uploadedFile);
    });
    this.props.actions.showMessageAction('File uploaded!');
  };

  handleDrop = (files, rejectedFiles) => {
    if (rejectedFiles.length) {
      const file = rejectedFiles[0];
      if (this.props.maxSize && file.size > this.props.maxSize) {
        return this.props.actions.showErrorAction('File size is too large. Try a smaller one.');
      } else {
        return this.props.actions.showErrorAction('Invalid file!');
      }
    }
    if (!files[0]) {
      this.props.actions.showErrorAction('Invalid file!');
      return;
    }
    if (!this.props.withCrop) {
      this.uploadBlob(files[0], files[0]?.name);
    }
    this.setState({
      uploadedFiles: [],
      selectedFiles: files,
      error: null,
      progress: null,
    });
  };

  handleCrop = () => {
    const file = this.state.selectedFiles[0];
    this.cancelCrop();
    const croppedCanvas = this.cropper.cropper.getCroppedCanvas();
    const maxDimension = 1080 * 2;
    const { quality } = this.props;
    if (croppedCanvas.width > maxDimension || croppedCanvas.height > maxDimension) {
      const Pica = new pica(); // eslint-disable-line new-cap
      const resizedCanvas = document.createElement('canvas');
      const scalingRatio =
        croppedCanvas.width > croppedCanvas.height
          ? maxDimension / croppedCanvas.width
          : maxDimension / croppedCanvas.height;
      resizedCanvas.width = croppedCanvas.width * scalingRatio;
      resizedCanvas.height = croppedCanvas.height * scalingRatio;
      const alpha = file.type === 'image/png';
      Pica.resize(croppedCanvas, resizedCanvas, { alpha }).then((rc) =>
        rc.toBlob((blob) => this.uploadBlob(blob, file.name), file.type, quality)
      );
    } else {
      croppedCanvas.toBlob((blob) => this.uploadBlob(blob, file.name), file.type, quality);
    }
  };

  uploadBlob = (blob, fileName) => {
    const processedImage = new File([blob], S3Upload.prototype.scrubFilename(fileName), { type: blob.type });

    const getSignedUrl = getGetSignedUrl({
      acl: this.props.s3Acl,
      bucket: this.props.s3Bucket,
    });

    const options = {
      files: [processedImage],
      getSignedUrl,
      contentDisposition: this.props.contentDisposition || 'auto',
      uploadRequestHeaders: { 'x-amz-acl': this.props.s3Acl },
      onFinishS3Put: this.handleFinish,
      onProgress: this.handleProgress,
      onError: this.handleError,
    };
    new S3Upload(options); // eslint-disable-line no-new
  };

  cancelCrop = () => {
    this.setState({ selectedFiles: [] });
  };

  render() {
    const {
      fixed,
      acceptedTypes,
      withCrop,
      maxSize,
      cropRatio = 16 / 9,
      isZoomable,
      name,
      formError,
    } = this.props;
    if (this.state.selectedFiles.length > 0 && withCrop) {
      const square = cropRatio === 1;
      return (
        <Dialog open maxWidth="md">
          <DialogTitle>Adjust image</DialogTitle>
          <DialogContent>
            <Cropper
              src={this.state.selectedFiles[0].preview}
              ref={(ref) => {
                this.cropper = ref;
              }}
              guides={isZoomable}
              zoomable={isZoomable}
              rotatable={false}
              scalable={false}
              minCropBoxWidth={fixed && !square ? 600 : 100}
              minCropBoxHeight={fixed && !square ? 337.5 : 100}
              viewMode={isZoomable ? 0 : 2}
              autoCropArea={1}
              dragMode="move"
              aspectRatio={fixed ? cropRatio : null}
              style={{ width: '100%', height: isZoomable ? 464 : 505 }}
            />
            {isZoomable && (
              <div style={{ display: 'flex', alignItems: 'center' }}>
                <p style={{ fontWeight: 'medium', marginRight: 5 }}>Zoom:</p>
                <IconButton
                  size="small"
                  color="primary"
                  onClick={() => this.cropper.cropper.zoom(0.1)}
                >
                  <AddBox />
                </IconButton>
                <IconButton
                  size="small"
                  color="primary"
                  onClick={() => this.cropper.cropper.zoom(-0.1)}
                >
                  <IndeterminateCheckBox />
                </IconButton>
              </div>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={this.cancelCrop}>Cancel</Button>
            <Button variant="contained" onClick={this.handleCrop}>
              Crop
            </Button>
          </DialogActions>
        </Dialog>
      );
    }
    return (
      <>
        {this.state.error && <div style={{ color: red }}>{this.state.error}</div>}
        <Dropzone
          name={name}
          onDrop={this.handleDrop}
          maxSize={maxSize}
          multiple={false}
          accept={acceptedTypes}
          style={{
            position: 'absolute',
            width: '100%',
            height: '100%',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            border: `2px dashed ${formError ? red : gray}`,
          }}
          activeStyle={{ border: `2px solid ${gray}` }}
          rejectStyle={{ border: `2px dashed ${orange}` }}
        >
          {typeof this.state.progress === 'number' ? (
            <CircularProgress />
          ) : (
            <div style={{ textAlign: 'center', color: formError ? red : gray }}>
              <Img
                src={ImageIcon}
                alt=""
                style={{
                  width: '25%',
                  filter:
                    formError &&
                    `invert(69%) sepia(49%) saturate(6332%) hue-rotate(332deg) brightness(95%) contrast(94%)`,
                }}
              />
              <br />
              {this.props.uploadCta}
            </div>
          )}
        </Dropzone>
      </>
    );
  }
}
FileUpload.propTypes = {
  name: PropTypes.string,
  formError: PropTypes.string,
  onFinish: PropTypes.func,
  actions: PropTypes.object,
  fixed: PropTypes.bool,
  withCrop: PropTypes.bool,
  cropRatio: PropTypes.number,
  acceptedTypes: PropTypes.oneOfType([PropTypes.string, PropTypes.arrayOf(PropTypes.string)]),
  maxSize: PropTypes.number,
  quality: PropTypes.number,
  contentDisposition: PropTypes.string,
  isZoomable: PropTypes.bool,
  uploadCta: PropTypes.string,
  s3Acl: PropTypes.oneOf(['private', 'public-read']),
  s3Bucket: PropTypes.oneOf(['primary', 'secure']),
};
FileUpload.defaultProps = {
  maxSize: 1024 * 1024 * 10, // 10MB
  quality: 0.7,
  uploadCta: 'Upload your file here',
  s3Acl: 'public-read',
  s3Bucket: 'primary',
  contentDisposition: 'auto',
  isZoomable: false,
};
export default connect(null, (dispatch) => ({
  actions: bindActionCreators(Actions, dispatch),
}))(FileUpload);
